import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Api } from '../api/api';
import {
  adminAuthControllerLoginV1,
  adminAuthControllerGetTokenNameV1,
  adminAuthControllerFindByTokenV1,
  adminAuthControllerLogoutV1,
} from '../api/functions';
import type { AuthRequestAdminCredentialsDto } from '../api/models/auth-request-admin-credentials-dto';
import type { UserResponseAdminUserDto } from '../api/models/user-response-admin-user-dto';
import { Observable, of, from, tap, concatMap, defer } from 'rxjs';
import { lastValueFrom } from 'rxjs';

/**
 * Authentication service for admin panel
 *
 * Manages authentication state using Angular signals and provides both
 * Observable and Promise-based APIs for maximum flexibility.
 *
 * Features:
 * - Signal-based reactive state management
 * - Cookie-based authentication with HTTP-only cookies
 * - Intelligent user caching to minimize API calls
 * - Computed signals for derived state
 * - Permission checking with auto-loading
 * - Comprehensive error handling and logging
 *
 * @example
 * ```typescript
 * // Login
 * await lastValueFrom(authService.login({ email, password }));
 *
 * // Check permission
 * const canDelete = await authService.hasPermission('ADMIN_USER_DELETE');
 *
 * // Access user
 * const user = authService.userSignal();
 * const isAuth = authService.isAuthenticated();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  // Dependencies
  private readonly api = inject(Api);
  private readonly router = inject(Router);

  // === Signals ===

  /**
   * User state signal - contains authenticated user data or null
   * Public writable signal for flexibility in edge cases
   */
  public readonly userSignal = signal<UserResponseAdminUserDto | null>(null);

  /**
   * Token name signal - stores the authentication cookie name
   */
  private readonly _tokenName = signal<string | undefined>(undefined);

  // === Computed Signals ===

  /**
   * Computed signal indicating if user is authenticated
   */
  public readonly isAuthenticated = computed(() => this.userSignal() !== null);

  /**
   * Computed signal with user's permissions array
   */
  public readonly permissions = computed(() => this.userSignal()?.adminPermissions ?? []);

  /**
   * Computed signal with user's email
   */
  public readonly userEmail = computed(() => this.userSignal()?.email);

  /**
   * Computed signal with user's username
   */
  public readonly username = computed(() => this.userSignal()?.username);

  /**
   * Computed signal indicating if user is a Super Admin
   */
  public readonly isSuperAdminSignal = computed(() => this.userSignal()?.isSuperAdmin ?? false);

  // === Public Properties ===

  /**
   * Token name (cookie name) - exposed as readonly computed signal
   */
  public readonly tokenName = this._tokenName.asReadonly();

  // === Public Methods ===

  /**
   * Get the token name (cookie name) from the backend
   * Automatically stores the result in the tokenName signal
   *
   * @returns Observable with token name response
   *
   * @example
   * ```typescript
   * authService.getTokenName().subscribe(response => {
   *   console.log('Token name:', response.tokenName);
   * });
   * ```
   */
  public getTokenName(): Observable<{ tokenName: string }> {
    return defer(() => from(this.api.invoke(adminAuthControllerGetTokenNameV1, {}))).pipe(
      tap((response) => {
        this._tokenName.set(response.tokenName);
      })
    );
  }

  /**
   * Check if the user is a Super Admin
   * Automatically fetches user data if not already loaded (lazy loading)
   *
   * Super Admins have access to all features and cannot be deleted.
   * This is the primary authorization check for the admin panel.
   *
   * @returns Promise<boolean> - true if user is a Super Admin
   *
   * @example
   * ```typescript
   * const isSuperAdmin = await authService.isSuperAdmin();
   * if (isSuperAdmin) {
   *   // Allow access to admin panel
   * }
   * ```
   */
  public async isSuperAdmin(): Promise<boolean> {
    let user = this.userSignal();

    // Lazy load user if not cached
    if (!user) {
      try {
        user = await lastValueFrom(this.findUserByApiToken());
      } catch (error) {
        // Error already displayed by interceptor
        return false;
      }
    }

    return !!user && user.isSuperAdmin === true;
  }

  /**
   * Check if the user has a specific permission
   * Automatically fetches user data if not already loaded (lazy loading)
   *
   * Note: Super Admins automatically have all permissions.
   * For fine-grained permission checks, use this method.
   * For admin panel access, use isSuperAdmin() instead.
   *
   * @param permission - Permission string to check (e.g., 'ADMIN_USER_DELETE')
   * @returns Promise<boolean> - true if user has the permission or is Super Admin
   *
   * @example
   * ```typescript
   * const canDelete = await authService.hasPermission('ADMIN_USER_DELETE');
   * if (canDelete) {
   *   // Allow delete operation
   * }
   * ```
   */
  public async hasPermission(permission: string): Promise<boolean> {
    let user = this.userSignal();

    // Lazy load user if not cached
    if (!user) {
      try {
        user = await lastValueFrom(this.findUserByApiToken());
      } catch (error) {
        // Error already displayed by interceptor
        return false;
      }
    }

    // Super Admins have all permissions
    if (user?.isSuperAdmin) {
      return true;
    }

    return !!user && user.adminPermissions.includes(permission as any);
  }

  /**
   * Check if user has any of the specified permissions (OR logic)
   *
   * @param permissions - Array of permission strings
   * @returns Promise<boolean> - true if user has at least one permission
   *
   * @example
   * ```typescript
   * const canManage = await authService.hasAnyPermission([
   *   'ADMIN_USER_CREATE',
   *   'ADMIN_USER_UPDATE'
   * ]);
   * ```
   */
  public async hasAnyPermission(permissions: string[]): Promise<boolean> {
    for (const permission of permissions) {
      if (await this.hasPermission(permission)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if user has all of the specified permissions (AND logic)
   *
   * @param permissions - Array of permission strings
   * @returns Promise<boolean> - true if user has all permissions
   *
   * @example
   * ```typescript
   * const hasFullAccess = await authService.hasAllPermissions([
   *   'ADMIN_USER_VIEW_ALL',
   *   'ADMIN_USER_DELETE'
   * ]);
   * ```
   */
  public async hasAllPermissions(permissions: string[]): Promise<boolean> {
    for (const permission of permissions) {
      if (!(await this.hasPermission(permission))) {
        return false;
      }
    }
    return true;
  }

  /**
   * Find user by API token (cookie-based authentication)
   *
   * Features:
   * - Intelligent caching: returns cached user if available
   * - Automatic token refresh on API fetch
   * - Errors handled by global interceptor
   *
   * @param forceRefresh - If true, bypasses cache and fetches from API
   * @returns Observable<UserResponseAdminUserDto>
   *
   * @example
   * ```typescript
   * // Use cached user if available
   * const user = await lastValueFrom(authService.findUserByApiToken());
   *
   * // Force fresh fetch
   * const user = await lastValueFrom(authService.findUserByApiToken(true));
   * ```
   */
  public findUserByApiToken(forceRefresh: boolean = false): Observable<UserResponseAdminUserDto> {
    return defer(() => {
      const cachedUser = this.userSignal();

      // Return cached user if available and not forcing refresh
      if (cachedUser && !forceRefresh) {
        return of(cachedUser);
      }

      // Fetch from API with token refresh
      return from(
        this.api.invoke(adminAuthControllerFindByTokenV1, {
          refresh: 'refresh',
        })
      ).pipe(
        concatMap((response) => {
          const user = response as unknown as UserResponseAdminUserDto;
          this.userSignal.set(user);
          return of(user);
        })
      );
    });
  }

  /**
   * Login with email and password
   *
   * On success:
   * - Stores user in userSignal
   * - Backend sets HTTP-only authentication cookie
   * - Returns user data
   *
   * On error:
   * - Error is displayed by global interceptor
   * - Observable throws error for component handling
   *
   * @param credentials - Admin login credentials (email, password, stayLoggedIn)
   * @returns Observable<UserResponseAdminUserDto> - Authenticated user data
   *
   * @example
   * ```typescript
   * authService.login({ email, password, stayLoggedIn: true })
   *   .subscribe({
   *     next: (user) => console.log('Logged in:', user.username),
   *     error: (err) => console.error('Login failed:', err)
   *   });
   * ```
   */
  public login(credentials: AuthRequestAdminCredentialsDto): Observable<UserResponseAdminUserDto> {
    return defer(() =>
      from(this.api.invoke(adminAuthControllerLoginV1, { body: credentials }))
    ).pipe(
      tap((response) => {
        this.userSignal.set(response.user);
      }),
      tap(() => {
        // Optionally fetch and store token name after login
        this.getTokenName().subscribe();
      }),
      // Extract user from response
      concatMap((response) => of(response.user))
    );
  }

  /**
   * Logout the current user
   *
   * Actions:
   * - Calls backend logout endpoint (clears cookie)
   * - Clears user signal on success
   * - Optionally redirects to login page
   * - Errors are displayed by global interceptor
   *
   * @param redirectToLogin - If true, navigates to /login after logout
   * @returns Observable<void>
   *
   * @example
   * ```typescript
   * // Logout without redirect
   * await lastValueFrom(authService.logout(false));
   *
   * // Logout with redirect
   * await lastValueFrom(authService.logout(true));
   * ```
   */
  public logout(redirectToLogin: boolean = false): Observable<void> {
    return defer(() => from(this.api.invoke(adminAuthControllerLogoutV1, {}))).pipe(
      tap(() => {
        this.userSignal.set(null);
        this._tokenName.set(undefined);

        if (redirectToLogin) {
          this.router.navigate(['/login']);
        }
      }),
      // Return void
      concatMap(() => of(undefined))
    );
  }

  /**
   * Initialize authentication state on app startup
   *
   * Attempts to restore user session from cookie by calling findUserByApiToken.
   * Should be called in app initialization (app.config.ts or APP_INITIALIZER)
   * Errors are displayed by global interceptor.
   *
   * @returns Promise<boolean> - true if user is authenticated, false otherwise
   *
   * @example
   * ```typescript
   * // In app.config.ts or main component
   * const authService = inject(AuthService);
   * const isAuthenticated = await authService.initializeAuth();
   *
   * if (!isAuthenticated) {
   *   router.navigate(['/login']);
   * }
   * ```
   */
  public async initializeAuth(): Promise<boolean> {
    try {
      const user = await lastValueFrom(this.findUserByApiToken());
      const isAuthenticated = !!user;

      return isAuthenticated;
    } catch (error) {
      // Error already displayed by interceptor
      return false;
    }
  }

  /**
   * Clear all authentication state
   * Useful for cleanup or manual state reset
   * Does NOT call backend logout endpoint
   *
   * @example
   * ```typescript
   * authService.clearAuthState();
   * ```
   */
  public clearAuthState(): void {
    this.userSignal.set(null);
    this._tokenName.set(undefined);
  }
}
